import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, formatApiError } from "../lib/api";
import { deriveKEK, generateMasterKey, encryptMasterKey, decryptMasterKey, setMasterKey, clearMasterKey } from "../lib/crypto";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking, false = guest, object = authed
  // We use `null` initial — meaning checking. Once resolved, will be `false` or user.

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        // me endpoint just sets user object. 
        // MK is already in sessionStorage if we just reloaded the page.
        setUser(res.data);
      })
      .catch(() => {
        tokenStore.clear();
        clearMasterKey();
        setUser(false);
      });
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      
      const kek = await deriveKEK(password, email);
      const mk = decryptMasterKey(data.user.encrypted_master_key, kek);
      setMasterKey(mk);
      
      tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const kek = await deriveKEK(password, email);
      const mk = generateMasterKey();
      const emk = encryptMasterKey(mk, kek);
      
      const { data } = await api.post("/auth/register", { email, password, name, encrypted_master_key: emk });
      setMasterKey(mk);
      
      tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    tokenStore.clear();
    clearMasterKey();
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
